import React,{useState,useEffect,useRef} from 'react';
import {supabase} from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import {processVideoUrl} from '../../utils/videoUtils';

const OwnerChat=({planId,client})=> {
const [messages,setMessages]=useState([]);
const [newMessage,setNewMessage]=useState('');
const [loading,setLoading]=useState(true);
const [sending,setSending]=useState(false);
const [messageError,setMessageError]=useState('');
const messagesEndRef=useRef(null);
const isMessagingEnabled=client?.messaging_enabled===true;

const fetchMessages=async ()=> {
try {
const {data,error}=await supabase
 .from('trainer_messages')
 .select('*')
 .eq('plan_id',planId)
 .order('created_at',{ascending: true});
if (error) throw error;
setMessages(data || []);
} catch (error) {
console.error('Error fetching messages:',error);
} finally {
setLoading(false);
}
};

useEffect(()=> {
fetchMessages();
const subscription=supabase
 .channel(`public:trainer_messages:plan_id=eq.${planId}`)
 .on('postgres_changes',{event: 'INSERT',schema: 'public',table: 'trainer_messages',filter: `plan_id=eq.${planId}`},(payload)=> {
setMessages(current=> {
if (current.some(msg=> msg.id===payload.new.id)) return current;
return [...current,payload.new];
});
})
 .subscribe();
return ()=> {
subscription.unsubscribe();
};
},[planId]);

useEffect(()=> {
messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
},[messages]);

const handleSendMessage=async (e)=> {
e.preventDefault();
if (!newMessage.trim() || sending || !isMessagingEnabled) return;
const messageToSend=newMessage.trim();
setNewMessage('');
setSending(true);
setMessageError('');
const tempId=`temp-${Date.now()}`;
const optimisticMessage={id: tempId,plan_id: planId,sender_type: 'owner',sender_name: client.owner_name,message_text: messageToSend,created_at: new Date().toISOString(),isOptimistic: true};
setMessages(current=> [...current,optimisticMessage]);
try {
const {data,error}=await supabase
 .from('trainer_messages')
 .insert([{plan_id: planId,sender_type: 'owner',sender_name: client.owner_name,message_text: messageToSend}])
 .select()
 .single();
if (error) throw error;
setMessages(current=> current.map(msg=> msg.id===tempId ? data : msg));
} catch (error) {
console.error('Error sending message:',error);
setMessages(current=> current.filter(msg=> msg.id !==tempId));
setNewMessage(messageToSend);
} finally {
setSending(false);
}
};

const VideoMessage=({msg})=> {
const url=msg.message_text.split(': ').slice(1).join(': ');
const embedUrl=processVideoUrl(url);
return (
 <div className="bg-white border rounded-2xl overflow-hidden shadow-sm max-w-xs mt-1">
 <div className="aspect-video bg-black flex items-center justify-center">
 {embedUrl ? (
 <iframe src={embedUrl} className="w-full h-full" allow="fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
 ) : (
 <div className="text-center p-4">
 <SafeIcon name="Video" className="w-8 h-8 text-gray-400 mx-auto mb-2" />
 <a href={url} target="_blank" className="text-xs text-indigo-600 font-bold underline">External Link</a>
 </div>
 )}
 </div>
 <div className="p-3 bg-indigo-50/50">
 <p className="text-xs font-bold text-indigo-900 truncate">{msg.message_text.split(': ')[0]}</p>
 </div>
 </div>
 );
};

return (
 <div className="flex flex-col h-[600px] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
 <div className="bg-gray-50 p-4 border-b border-gray-200">
 <h3 className="font-bold text-gray-800 flex items-center gap-2">
 <SafeIcon name="MessageCircle" className="w-5 h-5 text-indigo-600" /> Chat with Trainer
 </h3>
 </div>
 <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
 {loading ? (
 <div className="flex justify-center p-4 text-gray-400">Loading messages...</div>
 ) : messages.length===0 ? (
 <div className="text-center text-gray-400 py-8">
 {isMessagingEnabled ? "No messages yet. Ask your trainer a question!" : "No messages yet."}
 </div>
 ) : (
 messages.map((msg)=> {const isVideo=msg.message_text?.startsWith('🎥');return (
 <div key={msg.id} className={`flex ${msg.sender_type==='owner' ? 'justify-end' : 'justify-start'}`}>
 <div className={`max-w-[85%] flex flex-col ${msg.sender_type==='owner' ? 'items-end' : 'items-start'}`}>
 {isVideo ? (
 <VideoMessage msg={msg} />
 ) : (
 <div className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${msg.sender_type==='owner' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'}`}>
 <p>{msg.message_text}</p>
 </div>
 )}
 <div className="text-[10px] mt-1 opacity-70 px-1 text-gray-400">
 {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString([],{hour: '2-digit',minute: '2-digit'})}
 </div>
 </div>
 </div>
 );})
 )}
 <div ref={messagesEndRef} />
 </div>
 {isMessagingEnabled ? (
 <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-gray-200">
 <div className="flex gap-2">
 <input type="text" value={newMessage} onChange={(e)=> setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1 border rounded-full px-4 py-2.5 text-sm border-gray-300 focus:ring-1 focus:ring-indigo-500 outline-none" />
 <button type="submit" disabled={!newMessage.trim() || sending} className="bg-indigo-600 text-white p-2.5 rounded-full hover:bg-indigo-700 disabled:opacity-50">
 <SafeIcon name="Send" className="w-5 h-5" />
 </button>
 </div>
 </form>
 ) : (
 <div className="p-4 bg-gray-100 border-t border-gray-200 text-center text-xs text-gray-500">Messaging disabled.</div>
 )}
 </div>
 );
};
export default OwnerChat;